/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import classNames from 'classnames';
import React, { useContext } from 'react';

import {
  type PolymorphicCommonProps,
  type TooltipObjectProps,
} from '../_common/types';
import { Tooltip } from '../tooltip/Tooltip';

type SegmentedControlID = string | number;

type SegmentedControlProps<T extends SegmentedControlID> = {
  /** Additional class name */
  className?: string;
  /** Size of the segmented control */
  size?: 'small' | 'medium' | 'large';
  /** If segmented control items will include only icons (makes controls rectangular) */
  hasOnlyIcons?: boolean;
  /** The callback function triggered when the segmented control value changes */
  onChange: (e: T) => void | ((e: React.SetStateAction<T>) => void);
  /** The currently selected option */
  selected: T;
  /** The children component of the segmented control */
  children: React.ReactNode;
  /** Whether the segmented control has a box shadow applied */
  isFloating?: boolean;
};

type SegmentedControlContext<T extends SegmentedControlID> = Required<
  Pick<SegmentedControlProps<T>, 'onChange' | 'selected' | 'hasOnlyIcons'>
>;

// eslint-disable-next-line no-redeclare
const SegmentedControlContext =
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  React.createContext<SegmentedControlContext<any> | null>(null);

const useSelectorContext = () => {
  const context = useContext(SegmentedControlContext);

  if (context === null) {
    throw new Error('SegmentedControl used without context');
  }

  return context;
};

const SegmentedControlComponent = <
  T extends SegmentedControlID,
  U extends React.ElementType = 'div',
>({
  size = 'medium',
  hasOnlyIcons = false,
  children,
  selected,
  onChange,
  as,
  className,
  style,
  isFloating = false,
  htmlAttributes,
  ref,
  ...restProps
}: PolymorphicCommonProps<U, SegmentedControlProps<T>>) => {
  const classes = classNames('ndl-segmented-control', className, {
    'ndl-floating': isFloating,
    'ndl-large': size === 'large',
    'ndl-medium': size === 'medium',
    'ndl-small': size === 'small',
  });

  const Component = as ?? 'div';

  return (
    <Component
      className={classes}
      style={style}
      role="group"
      ref={ref}
      {...restProps}
      {...htmlAttributes}
    >
      <SegmentedControlContext.Provider
        value={{ hasOnlyIcons, onChange, selected }}
      >
        {children}
      </SegmentedControlContext.Provider>
    </Component>
  );
};

SegmentedControlComponent.displayName = 'SegmentedControl';

type SegmentedControlItemProps = {
  /** Additional class name */
  className?: string;
  /** The children component of the segmented control item */
  children?: React.ReactNode;
  /** The value of the segmented control item */
  value: string;
  /** The description of the segmented control item, will be displayed in the tooltip */
  description?: string;
  /** The tooltip props that will be applied if tooltip is set */
  tooltipProps?: TooltipObjectProps;
  /** Whether the segmented control item is disabled */
  isDisabled?: boolean;
};

const SegmentedControlItem = <T extends React.ElementType = 'button'>({
  as,
  children,
  className,
  style,
  value,
  htmlAttributes,
  description,
  tooltipProps,
  isDisabled = false,
  ref,
  ...restProps
}: PolymorphicCommonProps<T, SegmentedControlItemProps>) => {
  const { onChange, selected, hasOnlyIcons } = useSelectorContext();

  const isSelected = value === selected;

  const baseClasses = classNames(className, {
    'ndl-current': isSelected,
    'ndl-segment-icon': hasOnlyIcons,
    'ndl-segment-item': !hasOnlyIcons,
  });

  const Component: React.ElementType = as ?? 'button';

  return (
    <Tooltip
      isPortaled={false}
      isDisabled={Boolean(description) === false}
      {...tooltipProps?.root}
      type="simple"
    >
      <Tooltip.Trigger {...tooltipProps?.trigger} hasButtonWrapper>
        <Component
          aria-pressed={isSelected}
          onClick={() => !isSelected && onChange(value)}
          className={baseClasses}
          style={style}
          ref={ref}
          aria-label={description}
          disabled={isDisabled}
          {...restProps}
          {...htmlAttributes}
        >
          {children}
        </Component>
      </Tooltip.Trigger>
      <Tooltip.Content {...tooltipProps?.content}>
        <Tooltip.Body>{description}</Tooltip.Body>
      </Tooltip.Content>
    </Tooltip>
  );
};

SegmentedControlItem.displayName = 'SegmentedControl.Item';

const SegmentedControl = Object.assign(SegmentedControlComponent, {
  Item: SegmentedControlItem,
});

export { SegmentedControl };
